import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import MUICheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import PropTypes, { InferProps } from 'prop-types';

export const checkboxColors = {
  'no color': {
    swatch: 'transparent',
    tick: 'black',
    border: '#AAAAAA',
  },
  white: {
    swatch: '#FFFFF8',
    tick: 'black',
    border: '#999999',
  },
  beige: {
    swatch: 'beige',
    tick: 'black',
    border: '#D2D272',
  },
  yellow: {
    swatch: '#FFFF22',
    tick: 'black',
    border: '#FFE00B',
  },
  red: {
    swatch: 'crimson',
    tick: 'white',
    border: '#9a0e2a',
  },
  green: {
    swatch: 'forestgreen',
    tick: 'white',
    border: '#1A621B',
  },
  blue: {
    swatch: 'royalblue',
    tick: 'white',
    border: '#1e46be',
  },
  brown: {
    swatch: 'saddlebrown',
    tick: 'white',
    border: '#5a2d0c',
  },
  gray: {
    swatch: 'darkgray',
    tick: 'white',
    border: '#888888',
  },
  black: {
    swatch: 'black',
    tick: 'white',
    border: 'black',
  },
  gold: {
    swatch: 'gold',
    tick: 'black',
    border: '#FABC54',
  },
  orange: {
    swatch: '#FA8C00',
    tick: 'white',
    border: '#DE7A52',
  },
  pink: {
    swatch: '#f5a8cf',
    tick: 'white',
    border: '#F77AB3',
  },
  khaki: {
    swatch: 'khaki',
    tick: 'black',
    border: '#D4B48B',
  },
  purple: {
    swatch: 'darkorchid',
    tick: 'white',
    border: '#61287d',
  },
  silver: {
    swatch: 'silver',
    tick: 'black',
    border: '#AAAAAA',
  },
  multicolored: {
    swatch: 'transparent',
    tick: 'white',
    border: '#CCCCCC',
  },
  unrecognized: {
    swatch: 'transparent',
    tick: 'black',
    border: '#AAAAAA',
  },
};

const questionMark = css`
  :after {
    position: relative;
    left: 7px;
    font-family: var(--font-article);
    font-size: var(--size-sub1);
    content: '?';
  }
`;

const ColorFilterIcon = styled.span<{ name: keyof typeof checkboxColors }>`
  display: inline-block;
  position: absolute;
  z-index: -1;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  ${({ name }) => name === 'unrecognized' && questionMark}
  border: 1px solid ${({ name }) => (checkboxColors[name].border)};
  background-color: ${({ name }) => (checkboxColors[name].swatch)};
  background-image: ${({ name }) => (
    name === 'multicolored'
      // eslint-disable-next-line max-len
      ? 'linear-gradient(to right,#FFFF22,forestgreen,royalblue,darkorchid,violet,crimson,#FA8C00)'
      : 'none'
  )};
`;

export type ColorName = keyof typeof checkboxColors;

const checkCirclePropertyTypes = {
  checked: PropTypes.bool,
  name: PropTypes.string.isRequired,
};

type CheckCircleProperties = InferProps<typeof checkCirclePropertyTypes> & {
  name: ColorName;
};

const CheckCircle = ({ name, checked = false }: CheckCircleProperties) => (
  <>
    <ColorFilterIcon name={name} />
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="24"
      viewBox="0 0 24 24"
      width="24"
    >
      <g
        transform="scale(0.26 0.26)"
      >
        {/* eslint-disable-next-line max-len */}
        { checked && <path d="M 20 50 l 20 20 l 35 -35 l -6.5 -6.5 l -28.5 28.5 l -13.5 -13.5 L 20 50" fill={checkboxColors[name].tick} /> }
      </g>
    </svg>
  </>
);

CheckCircle.propTypes = checkCirclePropertyTypes;

const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiFormControlLabel-label {
    color: var(--black-medium);
    font-family: var(--font-primary);
    transform: none;
    letter-spacing: unset;
    margin: 2px 0 0px;
  }
  .MuiButtonBase-root {
    padding: 3px;
  }
`;

const colorCheckboxPropertyTypes = {
  checked: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
};

type ColorCheckboxProperties = InferProps<typeof colorCheckboxPropertyTypes> & {
  name: ColorName;
  checked?: boolean;
  onChange?: () => void;
  label?: string;
};

const ColorCheckbox = ({
  checked = false,
  label,
  name,
  onChange,
}: ColorCheckboxProperties) => (
  <StyledFormControlLabel
    control={(
      <MUICheckbox
        icon={<CheckCircle name={name} />}
        checkedIcon={(
          <CheckCircle
            name={name}
            checked
          />
        )}
        name={name}
        checked={checked}
        onChange={onChange}
        inputProps={{ 'aria-label': label }}
      />
    )}
    label={name}
  />
);

ColorCheckbox.propTypes = colorCheckboxPropertyTypes;

export default ColorCheckbox;
